import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Row, Col, Input, Checkbox, Form, Alert } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { trackEvent } from '../controllers/TrackingController';
import config from '../config';
import PublicLayout from '../comp/layouts/PublicLayout';
import ReCAPTCHA from 'react-google-recaptcha';
import CardCheckMessage from './UI/CardCheckMessage';
import { useGetPlans } from '../comp/hooks/query/customer';
import Presignup from './UI/Presignup';
import ChoosePlanInterval from '../comp/ui/ChoosePlanInterval';
import useMessage from '../comp/hooks/useMessage';

const NAME_REGEX = /^([^@#$^&%*()+=[\]/{}|:<>?,.])+$/;

const Join = () => {
  const intl = useIntl();
  const { search } = useLocation();
  const { message } = useMessage();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const paramPlan = queryParams.get('plan') || 'starter';
  const paramPromocode = queryParams.get('promocode');

  const { plans, isPlansLoading, IsPlansFetchError } = useGetPlans({ fromPlan: paramPlan });
  const plan = useMemo(() => plans?.find((p) => p.id === paramPlan), [paramPlan, plans]);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(undefined);
  const [isPresignup] = useState(config.env !== 'test');
  const [isPresignupSuccess, setIsPresignupSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params?.get('expiredtoken')) {
      message.warning({
        content: (
          <FormattedMessage defaultMessage="Your activation link has expired. You can register again to receive new one." />
        ),
      });
    }
  }, [search, message]);

  const onFinishFailed = useCallback(() => {
    message.warning({
      content: <FormattedMessage defaultMessage="Please fill out all fields" />,
    });
  }, [message]);

  const onFinish = useCallback(
    async (values) => {
      values.target = '/';
      values.isPodcaster = true;
      values.gCaptchaToken = captchaToken;
      delete values.agreeBox;

      setLoading(true);
      try {
        const { data } = await axios.post('/api/session/join', values);
        // tracking

        trackEvent({ category: 'signup', action: 'form-success', label: `join-${values.plan}` });

        if (!isPresignup) {
          // redirection
          message.success({
            content: (
              <FormattedMessage defaultMessage="Your account was created — welcome aboard!" />
            ),
            duration: 1,
            callback: () => window.location.replace(data?.target || '/'),
          });
        }

        setIsPresignupSuccess(true);
      } catch (error) {
        message.danger({
          content: error?.response?.data?.message || (
            <FormattedMessage defaultMessage="We could not create an account at this time" />
          ),
        });
      }

      setLoading(false);
    },
    [captchaToken, isPresignup, message]
  );

  return (
    <PublicLayout loading={isPlansLoading}>
      {IsPlansFetchError || (!isPlansLoading && !plan) ? (
        <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }} align="middle" justify="center">
          <Col span={24} className="gutter-bottom-lg">
            <Alert
              message={<FormattedMessage defaultMessage="Plan not found" />}
              description={
                <>
                  <p>
                    <FormattedMessage defaultMessage="The plan you are looking for does not exist" />
                  </p>
                  <Button href={`${config.corporateUrl}/pricing`}>
                    <FormattedMessage defaultMessage="Check other plans" />
                  </Button>
                </>
              }
              type="error"
              showIcon
            />
          </Col>
        </Row>
      ) : plans ? (
        <Form layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed} form={form}>
          <Row
            gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
            align="middle"
            justify="center"
            className="public__content"
          >
            <Col lg={12} md={22} sm={22} xs={22} className="gutter-bottom-lg">
              <Form.Item name="plan" initialValue={plan?.id}>
                <ChoosePlanInterval plans={plans} withFeatures />
              </Form.Item>

              {paramPromocode && (
                <div className="hidden">
                  <Form.Item type="hidden" name="promocode" initialValue={paramPromocode}>
                    <Input />
                  </Form.Item>
                </div>
              )}
            </Col>
            {isPresignupSuccess ? (
              <Presignup plan={plan} form={form} captchaToken={captchaToken} />
            ) : (
              <Col lg={12} md={22} sm={22} xs={22}>
                <Row
                  gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
                  type="flex"
                  justify="center"
                  align="middle"
                >
                  <Col sm={24}>
                    <Row
                      gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
                      className="gutter-bottom-lg"
                    >
                      <Col sm={24}>
                        <h2>
                          <FormattedMessage defaultMessage="Join us" />
                        </h2>
                      </Col>
                    </Row>
                    <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}>
                      <Col sm={12} xs={24}>
                        <Form.Item
                          name="firstname"
                          rules={[
                            {
                              type: 'string',
                              message: (
                                <FormattedMessage defaultMessage="This is not a valid name" />
                              ),
                            },
                            {
                              required: true,
                              message: (
                                <FormattedMessage defaultMessage="Please select a valid name" />
                              ),
                            },
                            {
                              max: 100,
                              message: (
                                <FormattedMessage
                                  defaultMessage="Please use fewer than {charactersMax} characters"
                                  values={{ charactersMax: '100' }}
                                />
                              ),
                            },
                            {
                              pattern: NAME_REGEX,
                              message: (
                                <FormattedMessage defaultMessage="Invalid character. Only latin character, numbers, spaces, dots and apostrophes are allowed" />
                              ),
                            },
                          ]}
                        >
                          <Input
                            size="large"
                            placeholder={intl.formatMessage({
                              defaultMessage: 'First name',
                            })}
                          />
                        </Form.Item>
                      </Col>

                      <Col sm={12} xs={24}>
                        <Form.Item
                          name="lastname"
                          rules={[
                            {
                              type: 'string',
                              message: (
                                <FormattedMessage defaultMessage="This is not a valid name" />
                              ),
                            },
                            {
                              required: true,
                              message: (
                                <FormattedMessage defaultMessage="Please select a valid name" />
                              ),
                            },
                            {
                              max: 100,
                              message: (
                                <FormattedMessage
                                  defaultMessage="Please use fewer than {charactersMax} characters"
                                  values={{ charactersMax: '100' }}
                                />
                              ),
                            },
                            {
                              pattern: NAME_REGEX,
                              message: (
                                <FormattedMessage defaultMessage="Invalid character. Only latin character, numbers, spaces, dots and apostrophes are allowed" />
                              ),
                            },
                          ]}
                        >
                          <Input
                            size="large"
                            placeholder={intl.formatMessage({
                              defaultMessage: 'Last name',
                            })}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}>
                      <Col span={24}>
                        <Form.Item
                          name="email"
                          rules={[
                            {
                              type: 'email',
                              message: (
                                <FormattedMessage defaultMessage="This is not a valid email" />
                              ),
                            },
                            {
                              required: true,
                              message: (
                                <FormattedMessage defaultMessage="Please select a valid email" />
                              ),
                            },
                            {
                              max: 100,
                              message: (
                                <FormattedMessage
                                  defaultMessage="Please use fewer than {charactersMax} characters"
                                  values={{ charactersMax: '100' }}
                                />
                              ),
                            },
                          ]}
                        >
                          <Input
                            size="large"
                            type="email"
                            prefix={<MailOutlined />}
                            placeholder={intl.formatMessage({
                              defaultMessage: 'Email',
                            })}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    {!isPresignup && (
                      <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}>
                        <Col span={24}>
                          <Form.Item
                            help={
                              <small>
                                <FormattedMessage defaultMessage="At least 8 characters, 1 letter, 1 digit & 1 special character" />
                              </small>
                            }
                            name="password"
                            rules={[
                              {
                                type: 'string',
                                message: (
                                  <FormattedMessage defaultMessage="This is not a valid password." />
                                ),
                              },
                              {
                                required: true,
                                message: (
                                  <FormattedMessage defaultMessage="Please select a valid password." />
                                ),
                              },
                              {
                                max: 30,
                                message: (
                                  <FormattedMessage
                                    defaultMessage="Please use fewer than {charactersMax} characters."
                                    values={{ charactersMax: '30' }}
                                  />
                                ),
                              },
                              {
                                min: 9,
                                message: (
                                  <FormattedMessage
                                    defaultMessage="Your password should be at least {charactersMin} characters."
                                    values={{ charactersMin: '9' }}
                                  />
                                ),
                              },
                              {
                                pattern: /\d/,
                                message: <FormattedMessage defaultMessage="Missing 1 digit" />,
                              },
                              {
                                pattern: /\W/,
                                message: (
                                  <FormattedMessage defaultMessage="Missing 1 special character" />
                                ),
                              },
                              {
                                pattern: /[a-zA-Z]/,
                                message: <FormattedMessage defaultMessage="Missing 1 letter" />,
                              },
                            ]}
                          >
                            <Input
                              size="large"
                              type="password"
                              prefix={<LockOutlined />}
                              placeholder={intl.formatMessage({
                                defaultMessage: 'Password',
                              })}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    <Row
                      gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
                      className="gutter-top-sm gutter-bottom-sm"
                    >
                      <Col span={24}>
                        <Form.Item
                          name="agreeBox"
                          valuePropName="checked"
                          rules={[
                            () => ({
                              validator: async (__validator, value) => {
                                if (!value) {
                                  throw new Error(
                                    intl.formatMessage({
                                      defaultMessage:
                                        'Please check this box to accept our terms and conditions and privacy policy.',
                                    })
                                  );
                                }
                              },
                            }),
                          ]}
                        >
                          <Checkbox>
                            <FormattedMessage
                              defaultMessage="By signing up you agree to our <termsLink>Terms and Conditions</termsLink> and <policyLink>Privacy Policy</policyLink>"
                              values={{
                                termsLink: function renderTermsLink(chunk) {
                                  return plan.isCreatorNetwork ? (
                                    <a
                                      href="//acast.com/terms-and-conditions-for-creators-advanced"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {chunk}
                                    </a>
                                  ) : (
                                    <a
                                      href="//acast.com/terms-and-conditions-for-creators-standard"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {chunk}
                                    </a>
                                  );
                                },
                                policyLink: function renderPolicyLink(chunk) {
                                  return (
                                    <a
                                      href="//acast.com/privacy"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {chunk}
                                    </a>
                                  );
                                },
                              }}
                            />
                          </Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row
                      gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
                      style={{ marginBottom: '20px' }}
                    >
                      <Col span={24}>
                        <ReCAPTCHA
                          sitekey={config.googleCaptchaSiteKey}
                          onChange={setCaptchaToken}
                        />
                      </Col>
                    </Row>
                    {plan?.isFreePlan && (
                      <Row gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}>
                        <Col span={24}>
                          <CardCheckMessage plan={plan} />
                        </Col>
                      </Row>
                    )}

                    <Row
                      gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
                      style={{ marginTop: '20px' }}
                      type="flex"
                      justify="space-around"
                      align="middle"
                    >
                      <Col span={24}>
                        <Button
                          block
                          loading={loading}
                          htmlType="submit"
                          size="large"
                          type="primary"
                          shape="round"
                          style={{ fontWeight: 700 }}
                        >
                          <FormattedMessage defaultMessage="Sign up" />
                        </Button>
                      </Col>
                    </Row>

                    <Row
                      gutter={{ xs: 14, sm: 14, md: 30, lg: 30, xxl: 30 }}
                      className="gutter-top-lg"
                    >
                      <Col span={24} className="center">
                        <FormattedMessage defaultMessage="Already have an account?" />{' '}
                        <a href="/signin" style={{ fontWeight: 700 }}>
                          <FormattedMessage defaultMessage="Log in" />
                        </a>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        </Form>
      ) : (
        <div />
      )}
    </PublicLayout>
  );
};

Join.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

export default Join;
